import { gql, useQuery } from '@apollo/client';
import { Switch as HeadlessSwitch } from '@headlessui/react';
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BuildingOfficeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { DialogContent, Tooltip } from '@mui/material';
import {
  DealTypeEnumType,
  FinancialInstrumentEnumType,
  FundingRoundNameEnumType,
  IndustryTypeEnumType,
  ValuationTypeEnumType,
} from '__generated__/graphql';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from 'primitives/Button';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';

import SummaryIcon from 'media/icons/summary.svg';
import SummaryLoadingAnimation from 'media/summary-animation.svg';

import constants from 'utils/constants';
import fundingRoundToReadable from 'utils/enums/funding-round-to-readable';
import industryToReadable from 'utils/enums/industry-to-readable';
import valuationTypeToReadable from 'utils/enums/valuation-type-to-readable';
import { formatCurrencyToShort, getDate, toTitleCase } from 'utils/format-helper';

import { useInvestPageNav } from '../InvestPage/InvestPageNavContext';

const DEAL_AI_SUMMARY_QUERY = gql`
  query DealAiSummary($id: ID!) {
    deal(id: $id) {
      id
      summary {
        description
        label
      }
    }
  }
`;

const LockedDealCard: FC<{
  companyName?: string;
  companyImage?: string;
  companyDescription?: string;
  industries?: string[];
  onApplyForAccess: () => void;
}> = ({ companyName, companyImage, companyDescription, industries = [], onApplyForAccess }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [imageError, setImageError] = useState(false);
  const hasImage = companyImage && !imageError;

  const cardShellClass =
    'bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-4 h-full min-h-[28rem] shadow-[0_1px_2px_rgba(16,24,40,.05),0_10px_28px_-14px_rgba(16,24,40,.18)]';

  const industryTagClass =
    'px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full';

  return (
    <div
      className="relative w-full h-full min-h-[28rem]"
      style={{ perspective: '1000px' }}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
    >
      <div
        className="relative w-full h-full min-h-[28rem] transition-transform duration-500"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front of card - Locked state (White/Blue) */}
        <div
          className={classNames(cardShellClass, 'relative w-full')}
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden filter blur-sm select-none opacity-50">
              {hasImage ? (
                <img
                  src={companyImage || constants.ALT_AVATAR_URL}
                  alt={companyName || 'Company'}
                  className="w-full h-full object-contain p-1"
                  onError={() => setImageError(true)}
                />
              ) : (
                <BuildingOfficeIcon className="h-5 w-5 text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="mt-0.5 text-[17px] font-bold leading-snug text-slate-900 filter blur-sm select-none opacity-50">
                {companyName || 'Undisclosed Company'}
              </p>
            </div>
          </div>

          <div className="text-center flex-1 flex items-center justify-center">
            <p className="text-[13.5px] leading-relaxed text-slate-600">
              {companyDescription || 'Investment opportunity available to accredited investors'}
            </p>
          </div>

          {industries && industries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center">
              {industries.slice(0, 2).map((industry, idx) => (
                <span key={idx} className={industryTagClass}>
                  {toTitleCase(industry)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Back of card - Hover state (Gray) */}
        <div
          className={classNames(cardShellClass, 'absolute inset-0 w-full')}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-slate-300">
              <LockClosedIcon className="h-6 w-6 text-slate-400" />
            </div>
          </div>

          <p className="text-[13.5px] leading-relaxed text-slate-600 text-center px-2">
            This deal is only available to accredited investors
          </p>

          <div>
            <Button
              variant="primary"
              className="w-full px-4 py-2 text-sm font-semibold"
              onClick={onApplyForAccess}
            >
              Apply for Access
            </Button>
          </div>

          {industries && industries.length > 0 && (
            <div className="flex flex-wrap gap-1.5 justify-center">
              {industries.slice(0, 2).map((industry, idx) => (
                <span key={idx} className={industryTagClass}>
                  {toTitleCase(industry)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CONVERTIBLE_INSTRUMENTS = [
  FinancialInstrumentEnumType.Ccds,
  FinancialInstrumentEnumType.ConvertibleNote,
  FinancialInstrumentEnumType.Safe,
];

const MAX_COINVESTOR_ROWS = 3;

/** Fixed section heights keep explore-deal cards aligned in the grid. */
const DEAL_CARD_SECTION = {
  header: 'h-11',
  description: 'h-[4.25rem]',
  industries: 'h-8',
  coInvestors: 'h-[5.75rem]',
  dealTerms: 'h-[7.5rem]',
  stats: 'h-[4.25rem]',
  footer: 'h-9',
} as const;

const DEAL_TERM_ROW_CLASS = 'flex h-10 flex-shrink-0 items-center justify-between';

type CoInvestor = { id: string; name: string; isLead: boolean };

type CoInvestorRow =
  | { type: 'investor'; investor: CoInvestor }
  | { type: 'overflow'; count: number; hiddenNames: string[] }
  | { type: 'empty' };

function buildCoInvestorRows(investors: CoInvestor[] = []): CoInvestorRow[] {
  const rows: CoInvestorRow[] = [];

  for (let i = 0; i < MAX_COINVESTOR_ROWS; i++) {
    if (investors.length > MAX_COINVESTOR_ROWS && i === MAX_COINVESTOR_ROWS - 1) {
      const hidden = investors.slice(MAX_COINVESTOR_ROWS - 1);
      rows.push({
        type: 'overflow',
        count: hidden.length,
        hiddenNames: hidden.map(investor => investor.name),
      });
    } else if (investors[i]) {
      rows.push({ type: 'investor', investor: investors[i] });
    } else {
      rows.push({ type: 'empty' });
    }
  }

  return rows;
}

const INSIGHTS_THINKING_STEPS = [
  { message: 'Pulling deal context', hint: 'Memo, pitch deck, and round details' },
  { message: 'Analyzing the opportunity', hint: 'Traction, terms, and market positioning' },
  { message: 'Crafting your summary', hint: 'Distilling highlights worth your attention' },
  { message: 'Almost ready', hint: 'Putting the final touches on your insights' },
] as const;

const AI_INSIGHTS_LOADING_MS = 4000;
const INSIGHTS_STEP_MS = 1000;

const ThinkingEllipsis: FC = () => (
  <span className="inline-flex min-w-[1.125rem] items-end gap-px pb-px" aria-hidden>
    {[0, 1, 2].map(delay => (
      <span
        key={delay}
        className="text-[17px] font-semibold leading-none text-indigo-500 motion-safe:animate-pulse"
        style={{ animationDelay: `${delay * 180}ms`, animationDuration: '1.1s' }}
      >
        .
      </span>
    ))}
  </span>
);

const AiInsightsThinkingSimulation: FC = () => {
  const [stepIndex, setStepIndex] = useState(0);
  const [labelVisible, setLabelVisible] = useState(true);
  const step = INSIGHTS_THINKING_STEPS[stepIndex];

  useEffect(() => {
    setStepIndex(0);
    const interval = setInterval(() => {
      setStepIndex(prev => Math.min(prev + 1, INSIGHTS_THINKING_STEPS.length - 1));
    }, INSIGHTS_STEP_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setLabelVisible(false);
    const timeout = setTimeout(() => setLabelVisible(true), 180);
    return () => clearTimeout(timeout);
  }, [stepIndex]);

  return (
    <div className="flex h-80 flex-col items-center justify-center px-8 py-10">
      <div className="relative h-[96px] w-[96px]">
        <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-violet-400/25 via-indigo-400/20 to-cyan-400/25 blur-lg animate-pulse" />
        <div className="relative flex h-[92px] w-[92px] items-center justify-center overflow-hidden rounded-full bg-white shadow-[0_8px_30px_rgba(99,102,241,0.18)] ring-1 ring-indigo-100/80">
          <img
            src={SummaryLoadingAnimation}
            alt=""
            className="h-[88px] w-[88px] object-contain"
            aria-hidden
          />
        </div>
      </div>

      <div
        className="mt-9 flex min-h-[4.5rem] flex-col items-center justify-center gap-2 text-center"
        aria-live="polite"
        aria-busy="true"
      >
        <p
          className={classNames(
            'flex items-center justify-center gap-1 transition-all duration-300 ease-out',
            labelVisible ? 'translate-y-0 opacity-100' : 'translate-y-1.5 opacity-0'
          )}
        >
          <span className="bg-gradient-to-r from-slate-800 via-indigo-900 to-slate-800 bg-clip-text text-[15px] font-semibold tracking-tight text-transparent">
            {step.message}
          </span>
          <ThinkingEllipsis />
        </p>
        <p
          className={classNames(
            'max-w-[16rem] text-[13px] leading-snug text-slate-500 transition-all duration-300 ease-out delay-75',
            labelVisible ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
          )}
        >
          {step.hint}
        </p>
      </div>
    </div>
  );
};

type ExploreDealDetailsProps = {
  companyName?: string;
  companyImage?: string;
  companyDescription?: string;
  industries?: string[];
  round?: FundingRoundNameEnumType | string;
  lastDate?: string;
  minInvestmentAmount?: number;
  financialInstrument?: FinancialInstrumentEnumType | string;
  valuationType?: ValuationTypeEnumType | string;
  valuationOrCap?: number;
  capPrice?: number;
  floorPrice?: number;
  externalInvestors?: Array<{ id: string; name: string; isLead: boolean }>;
};

type InsightsModalTab = 'insights' | 'details';

const ExploreDealModalDetailsPanel: FC<ExploreDealDetailsProps> = ({
  companyName,
  companyImage,
  companyDescription,
  industries = [],
  round,
  lastDate,
  minInvestmentAmount,
  financialInstrument,
  valuationType,
  valuationOrCap,
  capPrice,
  floorPrice,
  externalInvestors,
}) => {
  const [imageError, setImageError] = useState(false);
  const hasImage = companyImage && !imageError;
  const lastDateDisplay = lastDate ? dayjs(lastDate).format(`DD MMM'YY`) : '—';
  const isConvertible = CONVERTIBLE_INSTRUMENTS.includes(
    financialInstrument as FinancialInstrumentEnumType
  );
  const displayedIndustries = industries.slice(0, 2);
  const extraIndustries = industries.length - 3;
  const coInvestorRows = buildCoInvestorRows(externalInvestors ?? []);
  const hasCoInvestors = (externalInvestors?.length ?? 0) > 0;

  const dealTermRows = isConvertible
    ? [
        {
          label: 'Round',
          value: round ? fundingRoundToReadable(round as FundingRoundNameEnumType).label : '—',
        },
        {
          label: 'Cap',
          value: capPrice && capPrice > 0 ? formatCurrencyToShort(capPrice) : '—',
        },
        {
          label: 'Floor',
          value: floorPrice && floorPrice > 0 ? formatCurrencyToShort(floorPrice) : '—',
        },
      ]
    : [
        {
          label: 'Round',
          value: round ? fundingRoundToReadable(round as FundingRoundNameEnumType).label : '—',
        },
        {
          label: 'Val.',
          value:
            valuationOrCap && valuationOrCap > 0 ? (
              <>
                {formatCurrencyToShort(valuationOrCap)}
                {valuationType && (
                  <span className="font-normal text-slate-400 text-[11px] ml-1">
                    ({valuationTypeToReadable(valuationType as ValuationTypeEnumType).label})
                  </span>
                )}
              </>
            ) : (
              '—'
            ),
        },
      ];

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          {hasImage ? (
            <img
              src={companyImage || constants.ALT_AVATAR_URL}
              alt={companyName || 'Company'}
              className="h-full w-full object-contain p-1"
              onError={() => setImageError(true)}
            />
          ) : (
            <BuildingOfficeIcon className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <h3 className="flex-1 text-[17px] font-bold leading-snug text-slate-900">
          {companyName || 'Undisclosed Company'}
        </h3>
      </div>

      {companyDescription ? (
        <p className="text-[13.5px] leading-relaxed text-slate-600">{companyDescription}</p>
      ) : null}

      {displayedIndustries.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {displayedIndustries.map((industry, idx) =>
            industry ? (
              <span
                key={idx}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
              >
                {industryToReadable(industry as IndustryTypeEnumType).label}
              </span>
            ) : null
          )}
          {extraIndustries > 0 && (
            <Tooltip
              title={industries
                .slice(3)
                .map(i => industryToReadable(i as IndustryTypeEnumType).label)
                .join(', ')}
              placement="top"
            >
              <span className="cursor-default rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">
                +{extraIndustries}
              </span>
            </Tooltip>
          )}
        </div>
      )}

      {hasCoInvestors && (
        <>
          <hr className="border-slate-100" />
          <div className="space-y-2">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
              Co-investors
            </p>
            <div className="flex flex-col gap-2">
              {coInvestorRows
                .filter(row => row.type !== 'empty')
                .map((row, idx) => (
                  <CoInvestorRowItem key={`modal-co-investor-${idx}`} row={row} />
                ))}
            </div>
          </div>
        </>
      )}

      <hr className="border-slate-100" />
      <div className="divide-y divide-dashed divide-slate-100">
        {dealTermRows.map((row, idx) => (
          <div key={`modal-deal-term-${idx}`} className={DEAL_TERM_ROW_CLASS}>
            <span className="text-sm text-slate-500">{row.label}</span>
            <span className="text-sm font-semibold text-slate-900">{row.value}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-slate-50 px-3 py-2.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Min ticket
          </p>
          <p className="text-base font-bold leading-tight text-slate-900">
            {formatCurrencyToShort(minInvestmentAmount)}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2.5">
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Closes
          </p>
          <p className="text-base font-bold leading-tight text-slate-900">{lastDateDisplay}</p>
        </div>
      </div>
    </div>
  );
};

const AiInsightsTrigger: FC<{ onClick: () => void }> = ({ onClick }) => (
  <Tooltip title="Insights" placement="top">
    <button
      type="button"
      onClick={onClick}
      aria-label="Open AI Insights"
      className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 via-indigo-500 to-cyan-400 p-[2px] shadow-md shadow-indigo-500/20 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
    >
      <img src={SummaryIcon} alt="Summary" className="h-4 w-4" />
    </button>
  </Tooltip>
);

const CoInvestorRowItem: FC<{ row: CoInvestorRow }> = ({ row }) => {
  if (row.type === 'empty') {
    return <div className="h-8 flex-shrink-0" aria-hidden="true" />;
  }

  if (row.type === 'overflow') {
    return (
      <Tooltip title={row.hiddenNames.join(', ')} placement="top">
        <div className="flex h-8 min-h-8 flex-shrink-0 items-center px-0.5">
          <span className="truncate text-xs font-semibold text-slate-500">+{row.count} more</span>
        </div>
      </Tooltip>
    );
  }

  const { investor } = row;

  return (
    <div className="flex h-8 min-h-8 flex-shrink-0 items-center gap-2 min-w-0 px-0.5">
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-[9px] font-bold text-white">
        {investor.name.charAt(0).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs font-medium leading-none text-slate-700">
        {investor.name}
      </span>
      {investor.isLead && (
        <span className="flex-shrink-0 rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">
          Lead
        </span>
      )}
    </div>
  );
};

const AccreditedDealCard: FC<{
  companyName?: string;
  companyImage?: string;
  companyDescription?: string;
  industries?: string[];
  round?: FundingRoundNameEnumType | string;
  lastDate?: string;
  minInvestmentAmount?: number;
  financialInstrument?: FinancialInstrumentEnumType | string;
  valuationType?: ValuationTypeEnumType | string;
  valuationOrCap?: number;
  capPrice?: number;
  floorPrice?: number;
  externalInvestors?: Array<{ id: string; name: string; isLead: boolean }>;
  onViewDetails: () => void;
  onOpenInsights: () => void;
  hasCustomRules: boolean;
}> = ({
  companyName,
  companyImage,
  companyDescription,
  industries = [],
  round,
  lastDate,
  minInvestmentAmount,
  financialInstrument,
  valuationType,
  valuationOrCap,
  capPrice,
  floorPrice,
  externalInvestors,
  onViewDetails,
  onOpenInsights,
  hasCustomRules,
}) => {
  const [imageError, setImageError] = useState(false);
  const hasImage = companyImage && !imageError;

  const lastDateDisplay = lastDate ? dayjs(lastDate).format(`DD MMM'YY`) : '—';
  const isConvertible = CONVERTIBLE_INSTRUMENTS.includes(
    financialInstrument as FinancialInstrumentEnumType
  );
  const displayedIndustries = industries.slice(0, 3);
  const extraIndustries = industries.length - 3;
  const coInvestorRows = buildCoInvestorRows(externalInvestors ?? []);
  const hasCoInvestors = (externalInvestors?.length ?? 0) > 0;

  const dealTermRows = isConvertible
    ? [
        {
          label: 'Round',
          value: round ? fundingRoundToReadable(round as FundingRoundNameEnumType).label : '—',
        },
        {
          label: 'Cap',
          value: capPrice && capPrice > 0 ? formatCurrencyToShort(capPrice) : '—',
        },
        {
          label: 'Floor',
          value: floorPrice && floorPrice > 0 ? formatCurrencyToShort(floorPrice) : '—',
        },
      ]
    : [
        {
          label: 'Round',
          value: round ? fundingRoundToReadable(round as FundingRoundNameEnumType).label : '—',
        },
        {
          label: 'Val.',
          value:
            valuationOrCap && valuationOrCap > 0 ? (
              <>
                {formatCurrencyToShort(valuationOrCap)}
                {valuationType && (
                  <span className="font-normal text-slate-400 text-[11px] ml-1">
                    ({valuationTypeToReadable(valuationType as ValuationTypeEnumType).label})
                  </span>
                )}
              </>
            ) : (
              '—'
            ),
        },
        { label: '', value: '', isPlaceholder: true },
      ];

  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-col gap-3 h-full min-h-[28rem] shadow-[0_1px_2px_rgba(16,24,40,.05),0_10px_28px_-14px_rgba(16,24,40,.18)] hover:-translate-y-0.5 hover:shadow-[0_1px_2px_rgba(16,24,40,.05),0_22px_48px_-20px_rgba(16,24,40,.28)] hover:border-slate-300 transition-all duration-200">
      {/* Header */}
      <div className={classNames(DEAL_CARD_SECTION.header, 'flex flex-shrink-0 items-start gap-3')}>
        <div className="flex-shrink-0 w-11 h-11 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
          {hasImage ? (
            <img
              src={companyImage || constants.ALT_AVATAR_URL}
              alt={companyName || 'Company'}
              className="w-full h-full object-contain p-1"
              onError={() => setImageError(true)}
            />
          ) : (
            <BuildingOfficeIcon className="h-5 w-5 text-slate-400" />
          )}
        </div>
        <h3 className="flex-1 mt-0.5 text-[17px] font-bold leading-snug text-slate-900">
          {companyName || 'Undisclosed Company'}
        </h3>
        <button
          type="button"
          onClick={hasCustomRules ? onViewDetails : onOpenInsights}
          className="flex-shrink-0 w-9 h-9 rounded-[10px] border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
          aria-label="View deal"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Description */}
      <div className={classNames(DEAL_CARD_SECTION.description, 'flex-shrink-0 overflow-hidden')}>
        <p
          className={classNames(
            'text-[13.5px] leading-relaxed text-slate-600 line-clamp-3',
            !companyDescription && 'invisible'
          )}
        >
          {companyDescription || 'Placeholder description for layout alignment.'}
        </p>
      </div>

      {/* Sector tags */}
      <div
        className={classNames(
          DEAL_CARD_SECTION.industries,
          'flex flex-shrink-0 flex-wrap items-center gap-1.5 overflow-hidden'
        )}
      >
        {displayedIndustries.map((industry, idx) =>
          industry ? (
            <span
              key={idx}
              className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full"
            >
              {industryToReadable(industry as IndustryTypeEnumType).label}
            </span>
          ) : null
        )}
        {extraIndustries > 0 && (
          <Tooltip
            title={industries
              .slice(3)
              .map(i => industryToReadable(i as IndustryTypeEnumType).label)
              .join(', ')}
            placement="top"
          >
            <span className="px-2.5 py-1 bg-slate-100 text-slate-500 text-xs font-semibold rounded-full cursor-default">
              +{extraIndustries}
            </span>
          </Tooltip>
        )}
      </div>

      <hr className="border-slate-100 flex-shrink-0" />

      {/* Co-investors */}
      {hasCoInvestors && (
        <>
          <div className={classNames(DEAL_CARD_SECTION.coInvestors, 'flex flex-shrink-0 flex-col')}>
            <p
              className={classNames(
                'mb-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400',
                !hasCoInvestors && 'invisible'
              )}
            >
              Co-investors
            </p>
            <div className="flex flex-1 flex-col justify-center gap-2">
              {coInvestorRows.slice(0, 2).map((row, idx) => (
                <CoInvestorRowItem key={`co-investor-${idx}`} row={row} />
              ))}
            </div>
          </div>
          <hr className="border-slate-100 flex-shrink-0" />
        </>
      )}

      {/* Deal terms */}
      <div
        className={classNames(
          DEAL_CARD_SECTION.dealTerms,
          'flex flex-shrink-0 flex-col divide-y divide-dashed divide-slate-100'
        )}
      >
        {dealTermRows.map((row, idx) => (
          <div
            key={`deal-term-${idx}`}
            className={classNames(DEAL_TERM_ROW_CLASS, row.isPlaceholder && 'invisible')}
          >
            <span className="text-sm text-slate-500">{row.label || 'Placeholder'}</span>
            <span className="text-sm font-semibold text-slate-900">{row.value || '—'}</span>
          </div>
        ))}
      </div>

      {/* Stat tiles */}
      <div className={classNames(DEAL_CARD_SECTION.stats, 'grid flex-shrink-0 grid-cols-2 gap-2')}>
        <div className="flex h-full flex-col justify-center rounded-xl bg-slate-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
            Min ticket
          </p>
          <p className="text-base font-bold text-slate-900 leading-tight">
            {formatCurrencyToShort(minInvestmentAmount)}
          </p>
        </div>
        <div className="flex h-full flex-col justify-center rounded-xl bg-slate-50 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
            Closes
          </p>
          <p className="text-base font-bold text-slate-900 leading-tight">{lastDateDisplay}</p>
        </div>
      </div>

      {/* Footer */}
      <hr className="border-slate-100 flex-shrink-0 mt-auto" />
      <div className={classNames(DEAL_CARD_SECTION.footer, 'w-full')}>
        <Button
          variant="primary"
          onClick={hasCustomRules ? onViewDetails : onOpenInsights}
          className="w-full"
        >
          View Details
        </Button>
      </div>
    </article>
  );
};

export type ExploreDealData = {
  id: string;
  type?: DealTypeEnumType | null;
  companyName?: string;
  companyDescription?: string;
  companyImage?: string;
  companyWebsite?: string;
  lastDate: string;
  isAccredited: boolean;
  dealInviteLink?: string;
  investmentEntityId: string;
  isRequested?: boolean;
  industries?: string[];
  round?: FundingRoundNameEnumType | string;
  minInvestmentAmount?: number;
  financialInstrument?: FinancialInstrumentEnumType | string;
  valuationType?: ValuationTypeEnumType | string;
  valuationOrCap?: number;
  capPrice?: number;
  floorPrice?: number;
  externalInvestors?: Array<{ id: string; name: string; isLead: boolean }>;
  refetch: () => void;
  customEngagementRules?: string[];
};

const ExploreDealCard: FC<ExploreDealData> = ({
  id,
  companyName,
  companyDescription,
  companyImage,
  lastDate,
  isAccredited,
  dealInviteLink,
  industries,
  round,
  minInvestmentAmount,
  financialInstrument,
  valuationType,
  valuationOrCap,
  capPrice,
  floorPrice,
  externalInvestors,
  customEngagementRules,
}) => {
  const navigate = useNavigate();
  const { setHideTabHighlight } = useInvestPageNav();

  const [showAccreditationDialog, setShowAccreditationDialog] = useState(false);
  const [showInsightsDialog, setShowInsightsDialog] = useState(false);
  const [insightsModalTab, setInsightsModalTab] = useState<InsightsModalTab>('insights');
  const [termAcknowledged, setTermAcknowledged] = useState<boolean[]>([]);
  const [isProcessingSummary, setIsProcessingSummary] = useState(true);
  const insightsLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInsightsLoadingTimer = () => {
    if (insightsLoadingTimerRef.current) {
      clearTimeout(insightsLoadingTimerRef.current);
      insightsLoadingTimerRef.current = null;
    }
  };

  useEffect(() => () => clearInsightsLoadingTimer(), []);

  const { data: aiSummaryData, loading: isAiSummaryLoading } = useQuery(DEAL_AI_SUMMARY_QUERY, {
    variables: { id },
    skip: !showInsightsDialog,
  });

  const allTermLabels = useMemo(() => customEngagementRules ?? [], [customEngagementRules]);

  useEffect(() => {
    if (showAccreditationDialog) {
      setTermAcknowledged(allTermLabels.map(() => false));
    }
  }, [showAccreditationDialog, allTermLabels]);

  const allTermsAcknowledged =
    allTermLabels.length > 0 &&
    termAcknowledged.length === allTermLabels.length &&
    termAcknowledged.every(Boolean);

  const handleViewDeal = () => {
    setShowAccreditationDialog(false);
    setHideTabHighlight(false);
    if (dealInviteLink) {
      const host = window.location.origin;
      window.open(`${host}/deal-invite-links/${dealInviteLink}`, '_blank');
    }
  };

  const handleAccreditationClick = () => {
    setShowAccreditationDialog(true);
    setHideTabHighlight(true);
  };

  const handleOpenInsights = () => {
    clearInsightsLoadingTimer();
    setInsightsModalTab('insights');
    setIsProcessingSummary(true);
    setShowInsightsDialog(true);
    insightsLoadingTimerRef.current = setTimeout(() => {
      setIsProcessingSummary(false);
      insightsLoadingTimerRef.current = null;
    }, AI_INSIGHTS_LOADING_MS);
  };

  if (!isAccredited) {
    return (
      <div className="w-full h-full">
        <LockedDealCard
          companyName={companyName}
          companyImage={companyImage}
          companyDescription={companyDescription}
          industries={industries}
          onApplyForAccess={() => navigate('/invest/accreditation')}
        />
      </div>
    );
  }

  return (
    <>
      <div className="w-full min-w-0 h-full">
        <AccreditedDealCard
          companyName={companyName}
          companyImage={companyImage}
          companyDescription={companyDescription}
          industries={industries}
          round={round}
          lastDate={lastDate}
          minInvestmentAmount={minInvestmentAmount}
          financialInstrument={financialInstrument}
          valuationType={valuationType}
          valuationOrCap={valuationOrCap}
          capPrice={capPrice}
          floorPrice={floorPrice}
          externalInvestors={externalInvestors}
          hasCustomRules={(customEngagementRules?.length ?? 0) > 0}
          onViewDetails={handleAccreditationClick}
          onOpenInsights={handleOpenInsights}
        />
      </div>

      <Dialog
        open={showInsightsDialog}
        zIndexPriority="high"
        onClose={() => {
          clearInsightsLoadingTimer();
          setShowInsightsDialog(false);
          setIsProcessingSummary(true);
          setInsightsModalTab('insights');
        }}
      >
        <DialogPanel className="sm:max-w-lg overflow-hidden rounded-2xl border border-indigo-200/60 p-0 shadow-2xl ring-1 ring-indigo-500/10">
          {isProcessingSummary || isAiSummaryLoading ? (
            <DialogContent className="px-0 pb-0 pt-0">
              <AiInsightsThinkingSimulation />
            </DialogContent>
          ) : (
            <>
              <div className="bg-gradient-to-br from-violet-600 via-indigo-600 to-cyan-500 px-4 pb-0 pt-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
                      Deal overview
                    </p>
                    <h2 className="mt-0.5 truncate text-lg font-semibold text-white">
                      {companyName || 'Undisclosed Company'}
                    </h2>
                  </div>
                  <img src={SummaryIcon} alt="" className="h-9 w-9 flex-shrink-0 opacity-70" />
                </div>
                <div className="mt-4 flex gap-1">
                  {(
                    [
                      { id: 'insights' as const, label: 'AI Insights' },
                      { id: 'details' as const, label: 'Details' },
                    ] as const
                  ).map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setInsightsModalTab(tab.id)}
                      className={classNames(
                        'rounded-t-lg px-4 py-2.5 text-sm font-semibold transition-colors',
                        insightsModalTab === tab.id
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <DialogContent className="px-0 pb-0 pt-0">
                <div className="max-h-[min(420px,55vh)] overflow-y-auto py-4">
                  {insightsModalTab === 'insights' ? (
                    <ul className="space-y-3">
                      {aiSummaryData?.deal?.summary?.map(item => (
                        <li
                          key={item.label}
                          className="rounded-xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-3.5"
                        >
                          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                            {item.label}
                          </p>
                          <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                            {item.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ExploreDealModalDetailsPanel
                      companyName={companyName}
                      companyImage={companyImage}
                      companyDescription={companyDescription}
                      industries={industries}
                      round={round}
                      lastDate={lastDate}
                      minInvestmentAmount={minInvestmentAmount}
                      financialInstrument={financialInstrument}
                      valuationType={valuationType}
                      valuationOrCap={valuationOrCap}
                      capPrice={capPrice}
                      floorPrice={floorPrice}
                      externalInvestors={externalInvestors}
                    />
                  )}
                </div>
                <div className="space-y-3 border-t border-slate-100 bg-slate-50/90 px-4 py-4">
                  <Button
                    variant="primary"
                    className="w-full py-2.5 text-sm font-semibold shadow-sm"
                    onClick={() => {
                      handleViewDeal();
                    }}
                  >
                    View deal
                  </Button>
                  {insightsModalTab === 'insights' ? (
                    <p className="text-center text-xs leading-relaxed text-slate-500">
                      AI can make mistakes. Please verify important information before investing.
                    </p>
                  ) : null}
                </div>
              </DialogContent>
            </>
          )}
        </DialogPanel>
      </Dialog>

      {/* <Toast
        title={toastData?.title || ''}
        message={toastData?.message || ''}
        show={showToast}
        handleClose={handleToastClose}
        type={toastData?.type || 'success'}
      /> */}

      {/* -- Accreditation Dialog -- */}
      <Dialog
        open={showAccreditationDialog}
        zIndexPriority="high"
        onClose={() => {
          setShowAccreditationDialog(false);
          setHideTabHighlight(false);
        }}
      >
        <DialogPanel className="sm:max-w-2xl overflow-hidden rounded-2xl border border-slate-200/80 p-0 shadow-2xl ring-1 ring-slate-900/5">
          <div className="bg-gradient-to-br from-indigo-50/50 via-white to-slate-50/40 pb-5 pt-7 px-4">
            <DialogTitle>Confirm before viewing deal details</DialogTitle>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Please acknowledge the following terms before viewing deal details.
            </p>
          </div>
          <DialogContent className="px-0 pb-0 pt-0">
            <div className="max-h-[min(380px,55vh)] overflow-y-auto px-4 sm:px-8">
              <div className="divide-y divide-slate-100">
                {allTermLabels.map((label, idx) => (
                  <div
                    key={`${idx}-${label.slice(0, 24)}`}
                    className="flex gap-4 py-4 first:pt-2 sm:gap-5"
                  >
                    <p className="min-w-0 flex-1 text-sm leading-relaxed text-slate-600">{label}</p>
                    <HeadlessSwitch
                      aria-label={`Acknowledge statement ${idx + 1} of ${allTermLabels.length}`}
                      checked={termAcknowledged[idx] ?? false}
                      onChange={checked => {
                        setTermAcknowledged(prev => {
                          const next = [...prev];
                          while (next.length < allTermLabels.length) {
                            next.push(false);
                          }
                          next[idx] = checked;
                          return next;
                        });
                      }}
                      className={classNames(
                        termAcknowledged[idx] ? 'bg-indigo-600' : 'bg-slate-200',
                        'relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={classNames(
                          termAcknowledged[idx] ? 'translate-x-5' : 'translate-x-0',
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                        )}
                      />
                    </HeadlessSwitch>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-slate-100 bg-slate-50/90 px-4 py-5 sm:px-8">
              <Button
                variant="primary"
                className={classNames(
                  'w-full py-3 text-base font-semibold shadow-sm',
                  !allTermsAcknowledged && 'opacity-50 cursor-not-allowed'
                )}
                onClick={() => {
                  if (allTermsAcknowledged) {
                    handleOpenInsights();
                    setShowAccreditationDialog(false);
                    setHideTabHighlight(false);
                  }
                }}
              >
                View deal details
              </Button>
            </div>
          </DialogContent>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default ExploreDealCard;
